/**
 * templates/categories/creator.js - Creator-Focused Templates
 * Location: /app/frontend/src/admin/page_builder/templates/categories/
 * 
 * Templates for showcasing creators, rankings, and creator CTAs.
 * Uses Phase 1 primitives: layout presets, card variants.
 */

import { LayoutPreset } from '../../layout/presets';
import { BackgroundType, GradientDirection } from '../../layout/backgrounds';
import { CardVariant } from '../../cards/variants';

/**
 * Creator Spotlight - Featured creator profile card
 * Use cases: Homepage creator highlight, featured creator section
 */
export const creator_spotlight = {
  id: 'creator_spotlight',
  name: 'Creator Spotlight',
  category: 'creator',
  description: 'Featured creator profile with avatar, bio, and sample works.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.MEDIA_LEFT },
  background: { type: BackgroundType.NONE },
  cardVariant: CardVariant.CREATOR,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.MEDIA_LEFT, LayoutPreset.MEDIA_RIGHT, LayoutPreset.SPLIT_50_50],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID, BackgroundType.GRADIENT],
  allowedCardVariants: [CardVariant.CREATOR],
  maxBlocks: 2, // Can have up to 2 creator spotlights
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.MEDIA_LEFT },
  defaultBackground: { type: BackgroundType.NONE },
  
  blockType: 'section_creator_spotlight',
  defaultProps: {
    heading: 'Creator Spotlight',
    showHeading: true,
    creatorId: '', // Specific creator or 'featured' for auto-selection
    showAvatar: true,
    showBio: true,
    showStats: true, // Followers, works count
    showSampleWorks: true,
    sampleWorksCount: 3,
  },
  
  editableFields: [
    { key: 'heading', label: 'Section Heading', type: 'text' },
    { key: 'showHeading', label: 'Show Heading', type: 'boolean' },
    { key: 'creatorId', label: 'Creator ID (blank for featured)', type: 'text' },
    { key: 'showAvatar', label: 'Show Avatar', type: 'boolean' },
    { key: 'showBio', label: 'Show Bio', type: 'boolean' },
    { key: 'showStats', label: 'Show Stats', type: 'boolean' },
    { key: 'showSampleWorks', label: 'Show Sample Works', type: 'boolean' },
    { key: 'sampleWorksCount', label: 'Sample Works Count', type: 'number', min: 1, max: 6 },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/creators/featured',
    params: ['creatorId'],
  },
};

/**
 * Creator Grid - Grid of creator cards
 * Use cases: Creators page, top creators section
 */
export const creator_grid = {
  id: 'creator_grid',
  name: 'Creator Grid',
  category: 'creator',
  description: 'Multi-column grid of creator profile cards.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { type: BackgroundType.NONE },
  cardVariant: CardVariant.CREATOR,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID],
  allowedCardVariants: [CardVariant.CREATOR],
  maxBlocks: 2,
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.NONE },
  
  blockType: 'section_creator_grid',
  defaultProps: {
    heading: 'Creators',
    showHeading: true,
    columns: 4,
    maxItems: 8,
    sortBy: 'followers', // 'followers', 'works', 'recent', 'trending'
    showFollowButton: true,
    showWorksCount: true,
  },
  
  editableFields: [
    { key: 'heading', label: 'Section Heading', type: 'text' },
    { key: 'showHeading', label: 'Show Heading', type: 'boolean' },
    { key: 'columns', label: 'Columns', type: 'number', min: 2, max: 6 },
    { key: 'maxItems', label: 'Max Creators', type: 'number', min: 4, max: 24 },
    { key: 'sortBy', label: 'Sort By', type: 'select', options: ['followers', 'works', 'recent', 'trending'] },
    { key: 'showFollowButton', label: 'Show Follow Button', type: 'boolean' },
    { key: 'showWorksCount', label: 'Show Works Count', type: 'boolean' },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/creators',
    params: ['maxItems', 'sortBy'],
  },
};

/**
 * Creator CTA - Call-to-action for creators to join
 * Use cases: Homepage recruitment, creator landing page
 */
export const creator_cta = {
  id: 'creator_cta',
  name: 'Creator CTA',
  category: 'creator',
  description: 'Call-to-action section encouraging creators to join the platform.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SPLIT_60_40 },
  background: { 
    type: BackgroundType.GRADIENT,
    config: {
      direction: GradientDirection.TO_BOTTOM_RIGHT,
      from: 'rgba(249, 115, 22, 0.15)',
      to: 'transparent'
    },
    overlayOpacity: 0
  },
  cardVariant: null,
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE, LayoutPreset.SPLIT_60_40, LayoutPreset.SPLIT_50_50],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID, BackgroundType.GRADIENT],
  maxBlocks: 1, // Only one creator CTA per page
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SPLIT_60_40 },
  defaultBackground: { type: BackgroundType.GRADIENT },
  
  blockType: 'section_creator_cta',
  defaultProps: {
    heading: 'Start Creating Today',
    subheading: 'Join thousands of creators sharing their stories with the world.',
    ctaLabel: 'Become a Creator',
    ctaUrl: '/creator-signup',
    features: [
      'Keep 100% of your earnings',
      'Direct connection with readers',
      'Powerful creator tools',
    ],
    showFeatures: true,
    showStats: false,
    statsLabel: '',
  },
  
  editableFields: [
    { key: 'heading', label: 'Heading', type: 'text' },
    { key: 'subheading', label: 'Subheading', type: 'textarea' },
    { key: 'ctaLabel', label: 'CTA Label', type: 'text' },
    { key: 'ctaUrl', label: 'CTA URL', type: 'text' },
    { key: 'showFeatures', label: 'Show Features List', type: 'boolean' },
    { key: 'showStats', label: 'Show Stats', type: 'boolean' },
    { key: 'statsLabel', label: 'Stats Label', type: 'text' },
  ],
};

/**
 * Leaderboard Strip - Top creators/content rankings
 * Use cases: Homepage leaderboard, top charts
 */
export const leaderboard_strip = {
  id: 'leaderboard_strip',
  name: 'Leaderboard Strip',
  category: 'creator',
  description: 'Horizontal strip showing top ranked creators or content.',
  thumbnail: null,
  
  layout: { preset: LayoutPreset.SINGLE },
  background: { type: BackgroundType.NONE },
  cardVariant: CardVariant.CREATOR, // Can switch to DISCOVERY for content
  
  // Template constraints
  allowedLayouts: [LayoutPreset.SINGLE],
  allowedBackgrounds: [BackgroundType.NONE, BackgroundType.SOLID],
  allowedCardVariants: [CardVariant.CREATOR, CardVariant.DISCOVERY],
  maxBlocks: 2,
  
  // Defaults
  defaultLayout: { preset: LayoutPreset.SINGLE },
  defaultBackground: { type: BackgroundType.NONE },
  
  blockType: 'section_leaderboard_strip',
  defaultProps: {
    heading: 'Top Creators',
    showHeading: true,
    leaderboardType: 'creators', // 'creators', 'comics', 'novels', 'artwork'
    maxItems: 10,
    ranked: true, // Show rank numbers (1st, 2nd, 3rd styling)
    showScore: false, // Show follower count, views, etc.
    scoreLabel: 'followers',
    timeRange: 'all', // 'week', 'month', 'all'
    showMedals: true, // Gold, silver, bronze for top 3
  },
  
  editableFields: [
    { key: 'heading', label: 'Section Heading', type: 'text' },
    { key: 'showHeading', label: 'Show Heading', type: 'boolean' },
    { key: 'leaderboardType', label: 'Leaderboard Type', type: 'select', options: ['creators', 'comics', 'novels', 'artwork'] },
    { key: 'maxItems', label: 'Max Items', type: 'number', min: 3, max: 20 },
    { key: 'ranked', label: 'Show Rank Numbers', type: 'boolean' },
    { key: 'showScore', label: 'Show Score', type: 'boolean' },
    { key: 'scoreLabel', label: 'Score Label', type: 'select', options: ['followers', 'views', 'likes', 'reads'] },
    { key: 'timeRange', label: 'Time Range', type: 'select', options: ['week', 'month', 'all'] },
    { key: 'showMedals', label: 'Show Medals (Top 3)', type: 'boolean' },
  ],
  
  dataSource: {
    type: 'api',
    endpoint: '/api/leaderboard',
    params: ['leaderboardType', 'maxItems', 'timeRange'],
  },
  
  // Card variant switches based on leaderboardType
  dynamicCardVariant: {
    creators: CardVariant.CREATOR,
    comics: CardVariant.DISCOVERY,
    novels: CardVariant.DISCOVERY,
    artwork: CardVariant.DISCOVERY,
  },
};

export default {
  creator_spotlight,
  creator_grid,
  creator_cta,
  leaderboard_strip,
};
